import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ClipboardList } from 'lucide-react';
import { useStaffAuth } from '../../context/StaffAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_LABELS = {
  cash: 'Cash', card_pos: 'Card POS', upi_qr: 'UPI QR',
  bank_transfer: 'Bank', internal_adj: 'Internal',
};

export default function StaffBookings() {
  const { staff } = useStaffAuth();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (paymentFilter) params.payment_method = paymentFilter;
      const res = await axios.get(`${BACKEND_URL}/api/staff/bookings`, {
        params,
        withCredentials: true,
      });
      setRows(res.data.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [paymentFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="staff-bookings">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-7 h-7 text-[#1B375B]" />
          <div>
            <h1 className="text-2xl font-bold text-[#1B375B]">Bookings</h1>
            <p className="text-sm text-gray-500">
              {staff?.role === 'manager' ? 'All branch counter sales' : 'Your counter bookings'}
            </p>
          </div>
        </div>
        <select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm"
        >
          <option value="">All payment methods</option>
          {Object.entries(PAYMENT_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {location.state?.success && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
          Booking confirmed{location.state.bookingId ? `: ${location.state.bookingId}` : ''}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#659A9B]" /></div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[10px] uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Booking ID</th>
                <th className="px-4 py-3">Route</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id || b.booking_id} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-mono text-xs">{b.booking_id}</td>
                  <td className="px-4 py-3">{b.route || `${b.flight_data?.from || ''} → ${b.flight_data?.to || ''}`}</td>
                  <td className="px-4 py-3">{b.walkin_contact?.name || b.contact_phone || '—'}</td>
                  <td className="px-4 py-3">{PAYMENT_LABELS[b.payment_method] || b.payment_method || '—'}</td>
                  <td className="px-4 py-3 font-semibold">₹{Number(b.total_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3">{b.status || b.booking_status || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{String(b.created_at || '').slice(0, 10)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">No bookings yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
